import React, { useState, useEffect } from "react";
import ChatBotOption from "./ChatBotOption";
import PhoneNumberField from "./PhoneNumberField";
import { useDispatch, useSelector } from "react-redux";
import { useMediaQuery } from "react-responsive";
import {
  closeLoginModal,
  openforgotPasswordModal,
  openMobileOtpModal,
  openSignupModal,
} from "../../features/slices/ModalSlice";
import { updateSelectedItem } from "../../features/slices/CommonSlice";
import {
  loginUser,
  relogin,
  resetError,
  sendMobileOTP,
  storePhoneAndCountry,
  saveUserCreds,
  UNAVAILABLE_FEATURES,
} from "../../features/slices/UserSlice";
import {
  isPhoneNumberAcceptable,
  parsePhoneInput,
} from "../../utils/phoneValidation";
import {
  clearRememberedCredentials,
  getRememberedCredentials,
  saveRememberedCredentials,
  setRememberMePreference,
} from "../../utils/rememberMe";
import { createEmptyPin } from "../../utils/registrationConfig";
import {
  selectMpinDigits,
  selectMpinEnabled,
} from "../../features/slices/AppConfigSlice";

export default function LoginForm() {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user);
  const mpinDigits = useSelector(selectMpinDigits);
  const mpinEnabled = useSelector(selectMpinEnabled);
  const isMobile = useMediaQuery({ maxWidth: 768 });
  const [loginMode, setLoginMode] = useState("password");
  const [username, setUsername] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [password, setPassword] = useState("");
  const [phone, setPhone] = useState("");
  const [phoneE164, setPhoneE164] = useState("");
  const [countryCode, setCountryCode] = useState("");
  const [defaultCountry, setDefaultCountry] = useState("IN");
  const [isPhoneValid, setIsPhoneValid] = useState(false);
  const [mpin, setMpin] = useState(() => createEmptyPin(mpinDigits));
  const [localError, setLocalError] = useState("");
  const [rememberMe, setRememberMe] = useState(false);

  const isMpinMode = loginMode === "mpin";

  useEffect(() => {
    const remembered = getRememberedCredentials();
    if (!remembered) return;
    setUsername(remembered.username);
    setRememberMe(true);
  }, []);

  useEffect(() => {
    setMpin((prev) =>
      prev.length === mpinDigits ? prev : createEmptyPin(mpinDigits)
    );
  }, [mpinDigits]);

  useEffect(() => {
    if (!mpinEnabled && loginMode === "mpin") {
      setLoginMode("password");
    }
  }, [mpinEnabled, loginMode]);

  const handleRememberMeChange = (checked) => {
    setRememberMe(checked);
    if (!checked) clearRememberedCredentials();
  };

  // Must run before the login response is stored: it decides whether the auth
  // cookies are persistent or die with the tab.
  const persistRememberMePreference = (trimmedUsername) => {
    if (rememberMe) {
      saveRememberedCredentials({ username: trimmedUsername });
      setRememberMePreference(true);
    } else {
      clearRememberedCredentials();
    }
  };

  const handleUsernameChange = (event) => {
    setLocalError("");
    setUsername(event.target.value);
  };

  const togglePassword = () => {
    setShowPassword(!showPassword);
  };

  const handlePhoneNumberChange = (value) => {
    setLocalError("");
    const parsed = parsePhoneInput(value);
    setPhoneE164(parsed.e164);
    setIsPhoneValid(parsed.isValid);
    if (parsed.isValid) {
      if (parsed.country) setDefaultCountry(parsed.country);
      setCountryCode(parsed.countryCode);
      setPhone(parsed.nationalNumber);
    } else {
      setCountryCode("");
      setPhone("");
    }
  };

  const handlePinChange = (element, index) => {
    const actualValue = element.value;
    const value = element.value.replace(/[^0-9]/g, "");
    mpin[index] = value;
    setMpin([...mpin]);

    if (!actualValue.length) {
      if (element.previousSibling) element.previousSibling.focus();
      return;
    }

    if (value.length && element.nextSibling) element.nextSibling.focus();
  };

  const switchLoginMode = (mode) => {
    setLocalError("");
    dispatch(resetError());
    setLoginMode(mode);
  };

  const openForgotPasswordPopup = (initialOtpVerification = false) => {
    if (isMobile)
      dispatch(
        updateSelectedItem({
          name: "forgotPassword",
          initialOtpVerification: initialOtpVerification,
        })
      );
    else {
      dispatch(closeLoginModal());
      dispatch(openforgotPasswordModal(initialOtpVerification));
    }
  };

  const handlePasswordInput = (event) => {
    setPassword(event.target.value);
  };

  const openSignup = (e) => {
    e.preventDefault();
    if (isMobile) dispatch(updateSelectedItem("signup"));
    else {
      dispatch(openSignupModal());
      dispatch(relogin());
      dispatch(closeLoginModal());
    }
  };

  const loginWithPassword = (trimmedUsername) => {
    const payload = {
      username: trimmedUsername,
      password: password,
    };
    persistRememberMePreference(trimmedUsername);
    dispatch(storePhoneAndCountry({ username: trimmedUsername }));
    dispatch(
      saveUserCreds({
        username: trimmedUsername,
        password: password,
        grantType: "password",
      })
    );
    dispatch(loginUser(payload));
  };

  const loginWithMpin = (trimmedUsername) => {
    const mpinValue = mpin.join("");
    if (!isPhoneValid || !isPhoneNumberAcceptable(phoneE164)) {
      setLocalError("Phone number is not valid");
      return;
    }
    if (mpinValue.length !== mpinDigits) {
      setLocalError(`Please enter your ${mpinDigits}-digit MPIN`);
      return;
    }

    const payload = {
      username: trimmedUsername,
      phone,
      countryCode,
      mpin: mpinValue,
      grantType: "mpin",
    };
    persistRememberMePreference(trimmedUsername);
    dispatch(storePhoneAndCountry({ username: trimmedUsername, phone, countryCode }));
    dispatch(
      saveUserCreds({
        username: trimmedUsername,
        phone,
        countryCode,
        mpin: mpinValue,
        grantType: "mpin",
      })
    );
    dispatch(loginUser(payload));
  };

  const login = (event) => {
    const form = document.querySelector(".needs-validation");

    if (form.checkValidity()) {
      event.preventDefault();
      const trimmedUsername = username.trim();
      if (!trimmedUsername) {
        setLocalError("Username is required");
        return;
      }

      if (isMpinMode) {
        loginWithMpin(trimmedUsername);
      } else {
        loginWithPassword(trimmedUsername);
      }
    }
  };

  const closeLoginForm = () => {
    dispatch(updateSelectedItem(null));
  };

  useEffect(() => {
    if (user.userLoggedIn) {
      closeLoginForm();
    }
  }, [user.userLoggedIn]);

  const styles = {
    backgroundColor: isMobile ? "#111111" : "none",
    height: isMobile ? "100vh" : "auto",
  };

  const verifyMobileOtp = () => {
    if (UNAVAILABLE_FEATURES.mobileOtp) return;
    dispatch(resetError());
    dispatch(sendMobileOTP({ ...user.phoneDetails, flowSource: "login" }));
  };

  useEffect(() => {
    if (user.otpSent && user.otpFlowSource === "login") {
      dispatch(closeLoginModal());
      if (!isMobile) dispatch(openMobileOtpModal(false));
      else
        dispatch(
          updateSelectedItem({
            name: "mobileOTP",
            fromForgotPassword: false,
          })
        );
    }
  }, [user.otpSent, user.otpFlowSource, dispatch, isMobile]);

  return (
    <form className="needs-validation login-form">
      <div
        className={`${isMobile ? "form-mobile px-3 pt-3 pb-5" : ""}`}
        style={styles}
      >
        {isMobile && (
          <p className="text-center" style={{ color: "#EFC04B" }}>
            Login
          </p>
        )}

        {mpinEnabled && (
          <div className="login-form__mode-toggle mb-3">
            <button
              type="button"
              className={`login-form__mode-btn ${
                !isMpinMode ? "login-form__mode-btn--active" : ""
              }`}
              onClick={() => switchLoginMode("password")}
            >
              Password
            </button>
            <button
              type="button"
              className={`login-form__mode-btn ${
                isMpinMode ? "login-form__mode-btn--active" : ""
              }`}
              onClick={() => switchLoginMode("mpin")}
            >
              MPIN
            </button>
          </div>
        )}

        <div className="incorrect-details-div mb-3">
          {user.error ? (
            <p className="text-center message">
              {user.error}{" "}
              <span
                style={{ color: "#31b3ff", cursor: "pointer" }}
                className="verify-otp"
                onClick={verifyMobileOtp}
              >
                {user.error === "Please verify first otp for login." &&
                !UNAVAILABLE_FEATURES.mobileOtp
                  ? "Verify Now"
                  : ""}
              </span>
            </p>
          ) : null}
          {localError ? (
            <p className="text-center message">{localError} </p>
          ) : null}
          <ChatBotOption />
        </div>

        <b>
          Username
          <span aria-hidden="true" className="required-asterisk">
            {" *"}
          </span>
        </b>
        <input
          type="text"
          name="username"
          className="form-control text-input mb-3"
          placeholder="Enter username"
          value={username}
          onChange={handleUsernameChange}
          required
          autoComplete="username"
        />

        {isMpinMode ? (
          <>
            <b>
              Phone
              <span aria-hidden="true" className="required-asterisk">
                {" *"}
              </span>
            </b>
            <PhoneNumberField
              placeholder="Enter phone number"
              className={`mb-3 text-input ${
                isPhoneValid ? "is-valid form-control" : ""
              }`}
              onChange={handlePhoneNumberChange}
              defaultCountry={defaultCountry}
              autoComplete="tel"
              required
            />

            <b>
              MPIN
              <span aria-hidden="true" className="required-asterisk">
                {" *"}
              </span>
            </b>
            <div className="login-form__pin-row mb-3">
              {mpin.map((digit, index) => (
                <input
                  key={index}
                  className="login-form__pin-input"
                  type="password"
                  inputMode="numeric"
                  name="mpin"
                  maxLength="1"
                  value={digit}
                  onChange={(e) => handlePinChange(e.target, index)}
                  onFocus={(e) => e.target.select()}
                  required
                />
              ))}
            </div>
          </>
        ) : (
          <>
            <b>
              Password
              <span aria-hidden="true" className="required-asterisk">
                {" *"}
              </span>
            </b>
            <div className="password mb-3 text-input">
              <input
                type={`${showPassword ? "text" : "password"}`}
                name="password"
                id="password"
                onChange={handlePasswordInput}
                required
                placeholder="Enter Password"
                autoComplete="current-password"
              />
              <i
                className={`bi ${showPassword ? "bi-eye" : "bi-eye-slash"}`}
                id="togglePassword"
                style={{ fontSize: "18px", marginTop: "-3px" }}
                onClick={togglePassword}
              ></i>
            </div>
            <div className="d-flex w-100 justify-content-between">
              <label className="d-flex" style={{ cursor: "pointer" }}>
                <input
                  className="form-check-input mt-0"
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => handleRememberMeChange(e.target.checked)}
                  aria-label="Remember me"
                />
                <p className="remember-me" style={{ marginTop: "2px" }}>
                  Remember me
                </p>
              </label>
              <span
                className="forgot-password"
                onClick={() => openForgotPasswordPopup(false)}
              >
                Forgot Password?
              </span>
            </div>
          </>
        )}

        <button
          className="login-btn mb-3 mt-3 p-2"
          type="submit"
          onClick={login}
        >
          Login
        </button>
        <p className="text-center not--account">Don't have an account?</p>
        <button className="signup-btn mt-2 p-2" onClick={(e) => openSignup(e)}>
          Signup
        </button>
        {isMobile && (
          <button className="signup-btn mt-4 p-2" onClick={closeLoginForm}>
            Close
          </button>
        )}
      </div>
    </form>
  );
}
